/**
 * Gratitude: one appreciation a day, shown to the partner exactly as it was written.
 *
 * The only panel where that is true, and deliberately so — every other panel launders what you
 * said, and this is the one where saying it plainly IS the mechanism.
 *
 * THE PAGE IS ORDERED BY THE TRADE. A prompt if there is one, the box you write in, and then —
 * directly under it — the thing you get for writing: their card, veiled until you drop and their
 * words the moment you do. The insights and the history come after, because a run and a month are
 * measurements of the habit and the habit is the two cards above them.
 */
import { Empty } from '@/components/empty';
import { Hatch } from '@/components/face-down-card';
import { Panel } from '@/components/panel';
import { StampBand, STAMP_HOLD_MS } from '@/components/stamp-band';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/api';
import { dayLabel } from '@/lib/day';
import { useReducedMotion } from '@/lib/motion';
import { cn } from '@/lib/utils';
import { LoadFailed, PanelSkeleton } from '@/components/load-state';
import { useLayout } from '@/useLayout';
import { usePoll } from '@/usePoll';
import { useStore, whoLabel } from '@/store';
import * as React from 'react';
import { Animated, ScrollView, View } from 'react-native';

interface GratitudeEntry {
  id: string;
  author: 'A' | 'B';
  /**
   * Null when the server withheld it — see `veiled`. Never a placeholder and never a stripped
   * copy: the words are not on this device at all, which is what makes the veil a rule rather
   * than a rendering choice.
   */
  text: string | null;
  day: string;
  /**
   * Today's drop from the other side, held back until you have dropped yours.
   *
   * Decided SERVER-side, and it has to stay there. A client that received the text and merely
   * declined to draw it would be one screenshot, one devtools tab or one curl away from not
   * hiding it — so there is nothing here that could be talked into revealing anything.
   */
  veiled?: boolean;
  createdAt: string;
}

interface GratitudeState {
  entries: GratitudeEntry[];
  today: string;
  droppedToday: boolean;
  /** The run ending today — or ending yesterday, while today is still unwritten. */
  streak: number;
  /**
   * Drops this local month, per author.
   *
   * Optional in the TYPE only: the contract has it required, and this is what keeps a client that
   * is a deploy ahead of the API rendering a dash rather than "undefined this month". A month
   * nobody could read is not a month with nothing in it.
   */
  month?: { me: number; partner: number };
  /** The streak rule, from the server's own scoring config — see the run cell. */
  streakLength?: number;
  streakBonus?: number;
  /**
   * A prompt waiting above the box. Some are the app's own; some were planted by something the
   * other partner said in confidence. Nothing here says which, and nothing ever will.
   */
  prompt: { id: string; text: string } | null;
}

/** What the POST hands back: the run it just extended, and what it paid for doing so. */
interface DropResult {
  streak: number;
  points?: number;
  bonus?: number;
}

/**
 * The prompt, as an invitation rather than a caption.
 *
 * A tinted View rather than a `Card`, which is pinned to `bg-card` — the tint is the whole point.
 * `--accent` and not `--primary`: this is something to answer, not a button, and the app's one
 * colour means less every time something that is not an action takes it.
 */
function PromptCard({ text }: { text: string }) {
  return (
    <View className="bg-accent gap-2 rounded-lg p-5">
      <View className="flex-row flex-wrap items-center gap-2">
        <Text variant="caption" className="text-accent-foreground opacity-70">
          Today’s prompt
        </Text>
        {/**
         * Kept from the inline version this card replaced. Some prompts are the app's own and some
         * were planted by something the partner said in confidence — "from the game" is the one
         * wording that is true of both without ever saying which this one is.
         */}
        <Badge>
          <Text>from the game</Text>
        </Badge>
      </View>
      <Text variant="heading" className="text-accent-foreground">
        {text}
      </Text>
    </View>
  );
}

/** Caption, number, unit. Two of these are one row, so nothing here may be tall. */
function Cell({
  label,
  value,
  note,
  divided,
}: {
  label: string;
  value: string | number;
  note: string;
  divided?: boolean;
}) {
  return (
    <View className={cn('min-w-0 flex-1 gap-0.5', divided && 'border-border border-l pl-3')}>
      <Text variant="label">{label}</Text>
      {/* A run and a month are FIGURES: the display face, bold, tabular — so the row does not
          jitter as the numbers change under it. */}
      <Text variant="heading" className="tabular-nums font-bold">
        {value}
      </Text>
      {/* NOT clipped to one line. These notes are sentences now — "today still open", "one more
          lands +2" — and at 320 a `numberOfLines={1}` turned the pair of them into "THIS MO…" and
          "TODAY STILL…", which is a stat block that reports nothing. Two cells wrap; three did
          not fit at any width, which is the other half of why there are two. */}
      <Text variant="label">{note}</Text>
    </View>
  );
}

/**
 * The run, and the month the two of them had TOGETHER, as one row.
 *
 * It used to be three cells: your run, your month, their month. A month each is a scoreboard, on
 * the one panel in the app whose whole claim is that saying a kind thing is not a competition —
 * and it was the pair of cells that could not fit at 320 without being cut to "THIS MO…". One
 * joint figure says the thing worth knowing (this house said thirty-one kind things this month)
 * and takes nobody's side.
 *
 * The run's note is a STATE, not a unit. "7 days" alone is ambiguous every morning: is today
 * counted, or am I about to lose it? So it says which — still open until you drop, banked once you
 * have — and on the day before the bonus lands it says what is at stake instead.
 */
function Insights({
  streak,
  month,
  droppedToday,
  streakLength,
  streakBonus,
}: {
  streak: number;
  month?: { me: number; partner: number };
  droppedToday: boolean;
  streakLength?: number;
  streakBonus?: number;
}) {
  const together = month ? month.me + month.partner : null;
  /**
   * The day before a bonus lands, and only then.
   *
   * The rule and the figure both come from the server — a client carrying its own copy of "5" and
   * "+2" is a client that keeps promising a bonus the week after somebody retunes the scoring.
   * Without them the cell simply says whether today is banked, which is still true.
   */
  const nextBonusIn = streakLength ? streakLength - ((streak % streakLength) + (droppedToday ? 0 : 1)) : null;
  const runNote =
    streakBonus && nextBonusIn === 0
      ? `one more lands +${streakBonus}`
      : droppedToday
        ? 'banked'
        : 'today still open';

  return (
    <Card>
      <CardContent className="flex-row gap-3 p-4">
        <Cell label="Run" value={`${streak} ${streak === 1 ? 'day' : 'days'}`} note={runNote} />
        <Cell
          label="Together"
          value={together === null ? '—' : together}
          note={together === 1 ? 'drop this month' : 'drops this month'}
          divided
        />
      </CardContent>
    </Card>
  );
}

/**
 * WHAT TODAY'S DROP FROM THE OTHER SIDE LOOKS LIKE BEFORE YOU HAVE WRITTEN YOURS.
 *
 * The app's hatch and a lock, not grey bars. The text is not on this device, so there is nothing
 * here to blur — but the shape still has to say WHICH kind of absence this is, and ragged grey
 * bars are the shape a skeleton has. A reader who has learned "bars mean loading" waits in front
 * of a thing that is never coming, because it is not waiting on the network. It is waiting on
 * them. The hatch is the same mark the sealed agenda and the unrevealed week wear, and it means
 * the one thing: this exists and it is not yours yet.
 */
function Veil({ name }: { name: string }) {
  return (
    <View className="bg-card-2 border-border min-h-[92px] items-center justify-center gap-1.5 overflow-hidden rounded-md border p-4">
      <Hatch />
      <Text variant="heading" className="text-muted-foreground">
        ⌧
      </Text>
      <Text variant="small" className="text-muted-foreground text-center">
        Drop yours to read {name}’s
      </Text>
    </View>
  );
}

/**
 * The partner's card for today — the thing the drop is FOR.
 *
 * It cross-fades rather than swapping: the veil is what was on the screen a second ago, and a hard
 * cut leaves the reader unsure whether the words that appeared are the same object or a new one
 * that pushed the old one out. Both layers are drawn for the length of the fade, absolutely
 * stacked over a box the taller of them sizes, so nothing below moves while it runs.
 *
 * Under reduced motion it is a swap, which is the same information without the movement.
 */
function TodaysReply({
  name,
  text,
  hidden,
  reveal,
}: {
  name: string;
  text: string | null;
  hidden: boolean;
  /** True for the one render pass after a drop, which is what starts the fade. */
  reveal: boolean;
}) {
  const reduced = useReducedMotion();
  const fade = React.useRef(new Animated.Value(reveal ? 0 : 1)).current;
  const [veilOver, setVeilOver] = React.useState(reveal);

  React.useEffect(() => {
    if (!reveal || reduced) {
      fade.setValue(1);
      setVeilOver(false);
      return;
    }
    setVeilOver(true);
    fade.setValue(0);
    const anim = Animated.timing(fade, { toValue: 1, duration: 520, useNativeDriver: false });
    anim.start(({ finished }) => {
      if (finished) setVeilOver(false);
    });
    return () => anim.stop();
  }, [reveal, reduced, fade]);

  return (
    <Card>
      <CardContent className="gap-1">
        <Text variant="caption" className="text-muted-foreground">
          {name} · today
        </Text>
        {hidden ? (
          <Veil name={name} />
        ) : (
          <View>
            <Animated.View style={veilOver ? { opacity: fade } : undefined}>
              <Text>“{text}”</Text>
            </Animated.View>
            {veilOver ? (
              <Animated.View
                pointerEvents="none"
                style={[
                  { position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 },
                  { opacity: fade.interpolate({ inputRange: [0, 1], outputRange: [1, 0] }) },
                ]}>
                <Veil name={name} />
              </Animated.View>
            ) : null}
          </View>
        )}
      </CardContent>
    </Card>
  );
}

export function GratitudeScreen() {
  const { me, refreshBadges } = useStore();
  const { wide } = useLayout();
  const [state, setState] = React.useState<GratitudeState | null>(null);
  const [draft, setDraft] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  /**
   * The failure, ON THE BOX rather than in an alert.
   *
   * A drop that fails offline used to take the words with it — the draft was cleared before the
   * POST — and report the loss in a system alert somewhere else on the screen. The draft now
   * survives every failure, and the sentence explaining why sits under the button that did not
   * work, which is where somebody is already looking.
   */
  const [dropError, setDropError] = React.useState<string | null>(null);
  const [stamp, setStamp] = React.useState<string | null>(null);
  /** True for one render pass after a successful drop: what turns the veil into their words. */
  const [justDropped, setJustDropped] = React.useState(false);

  const body = React.useRef<ScrollView>(null);
  /** Where the partner's card sits in the scroller, measured as it lays out. */
  const replyY = React.useRef(0);

  const load = React.useCallback(async () => {
    setState(await api.get<GratitudeState>('/api/gratitude'));
  }, []);

  const { loaded, error, reload } = usePoll(load, 30_000);

  const partnerName = me?.partnerName ?? 'Them';
  const entries = state?.entries ?? [];
  const today = state?.today;
  /** Today's drop from the other side — the one entry the whole trade is about. */
  const theirsToday = entries.find((e) => e.author !== me?.partner && e.day === today) ?? null;
  const theirsHidden = !!theirsToday && (theirsToday.veiled === true || theirsToday.text === null);
  /** Everything else, newest first, exactly as the server ordered it. */
  const history = entries.filter((e) => e.id !== theirsToday?.id);

  /**
   * THE DROP, AS A MOMENT.
   *
   * Order matters and is the whole of this function: post, and only if the server agrees, clear
   * the box and stamp the card. A stamp is a reward for something that has happened — a "+1"
   * shown before the POST resolves is a lie the next refresh corrects, and on a train it is a lie
   * that costs somebody the sentence they wrote.
   */
  const drop = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setDropError(null);
    try {
      const result = await api.post<DropResult>('/api/gratitude', { text });
      const points = result?.points ?? 1;
      setStamp(`Dropped · +${points}`);
      setDraft('');
      await Promise.all([load(), refreshBadges()]);
      // The band holds on the card that is about to leave; then the page moves to what the drop
      // just bought and the veil turns into their words underneath it.
      setTimeout(() => {
        setStamp(null);
        setJustDropped(true);
        body.current?.scrollTo({ y: Math.max(0, replyY.current - 12), animated: true });
      }, STAMP_HOLD_MS);
    } catch (e) {
      // The words stay in the box. That is the point of catching it here rather than in `fail`.
      setDropError(e instanceof Error ? e.message : 'That did not send. Your words are still here.');
    } finally {
      setBusy(false);
    }
  };

  if (!loaded)
    return (
      <Panel id="gratitude">
        <PanelSkeleton rows={2} />
      </Panel>
    );
  if (error)
    return (
      <Panel id="gratitude">
        <LoadFailed onRetry={reload} reason={error} />
      </Panel>
    );
  if (!state) return <Panel id="gratitude">{null}</Panel>;

  const compose = state.droppedToday ? (
    <Card>
      <CardContent>
        <Text>Dropped for today. Tomorrow is another day.</Text>
      </CardContent>
    </Card>
  ) : (
    <Card>
      <CardContent>
        {/**
         * A CAPTION, not the header band. The band is 1370px of red across the top of a desktop
         * window, and it was winning the page over the box it introduces — a label for the
         * composer drawn louder than the composer. The one line of explanation that survives is
         * the card's own description; the ⓘ guide has the research, and the `WhyThis` footnote
         * that used to sit here expanded into a claim byte-identical to the guide's.
         */}
        {state.prompt ? null : (
          <View className="gap-1">
            <Text variant="caption" className="text-muted-foreground">
              Today’s drop
            </Text>
            <CardDescription>One thing they did that you appreciated. Small counts.</CardDescription>
          </View>
        )}
        {/* The box stops at a readable measure. A 1030px textarea three rows tall asks for an
            essay and gets one line running the width of a laptop. */}
        <Textarea
          value={draft}
          onChangeText={setDraft}
          maxLength={500}
          placeholder="They…"
          className="max-w-[560px]"
        />
        <Button
          className="self-start"
          disabled={!draft.trim() || busy}
          reason="Name one thing they did"
          onPress={drop}>
          <Text>Drop it</Text>
        </Button>
        {dropError ? (
          <Text variant="small" className="text-destructive">
            {dropError}
          </Text>
        ) : null}
      </CardContent>
      {stamp ? <StampBand kind="done" label={stamp} /> : null}
    </Card>
  );

  const reply = theirsToday ? (
    <TodaysReply
      name={partnerName}
      text={theirsToday.text}
      hidden={theirsHidden}
      reveal={justDropped && !theirsHidden}
    />
  ) : null;

  return (
    <Panel id="gratitude" bodyRef={body}>
      {state.prompt ? <PromptCard text={state.prompt.text} /> : null}

      {/**
       * THE BOX AND WHAT IT BUYS, SIDE BY SIDE where there is room.
       *
       * The partner's card was fourth on the page, under an insights row — so on a phone the
       * thing you are being asked to unlock was below the fold while you typed. Directly under
       * the box on a phone; beside it from bp-wide, inside the 880 column the panel already caps
       * everything to, so the pair reads as one exchange rather than as two unrelated cards.
       *
       * `wide` is `useLayout`'s answer and not a pixel: this file may not know a breakpoint.
       */}
      <View
        className={cn('gap-3', wide && 'flex-row items-start')}
        onLayout={(e) => {
          replyY.current = e.nativeEvent.layout.y;
        }}>
        <View className={cn('min-w-0', wide && 'flex-1')}>{compose}</View>
        {reply ? <View className={cn('min-w-0', wide && 'flex-1')}>{reply}</View> : null}
      </View>

      <Insights
        streak={state.streak}
        month={state.month}
        droppedToday={state.droppedToday}
        streakLength={state.streakLength}
        streakBonus={state.streakBonus}
      />

      {history.length ? (
        history.map((entry) => {
          /**
           * Either signal is enough. The contract says a withheld drop carries `veiled: true`
           * AND `text: null`; reading both means a server that ever sends only one of them
           * still cannot make this screen draw an empty pair of quotation marks where
           * somebody's words are supposed to be.
           */
          const hidden = entry.veiled === true || entry.text === null;
          const who = whoLabel(entry.author, me, { self: 'You' });
          return (
            /* The measure a run of remembered sentences is comfortable at — about 68 characters of
               the body step. In px rather than `ch`, which is a CSS unit a phone has no idea what
               to do with, exactly as `MEASURE_CLASS` is. */
            <Card key={entry.id} className="max-w-[544px]">
              <CardContent className="gap-1">
                {hidden ? <Veil name={who} /> : <Text>“{entry.text}”</Text>}
                <Text variant="small" className="text-muted-foreground">
                  {/* `day` is already the couple's own local calendar day, cut server-side — so it
                      goes straight to the label and never through `localDay`. */}
                  {who} · {dayLabel(entry.day)}
                </Text>
              </CardContent>
            </Card>
          );
        })
      ) : theirsToday ? null : (
        <Empty mark="♥" title="No drops yet" body="Someone go first. Name one specific thing — specific beats generous." />
      )}
    </Panel>
  );
}
